import styled from 'styled-components';
import { media } from '../../styles/theme';

export const ALTURA_PLOT = 200;
export const MARGEM_TOPO = 8;
export const ALTURA_ROTULO_X = 22;
export const LARGURA_EIXO_Y = 64;

export const Grafico = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.espaco.md};
  min-width: 0;
`;

// Eixo Y fora da área rolável + área do gráfico
export const AreaPlot = styled.div`
  display: flex;
  min-width: 0;
`;

export const EixoYContainer = styled.div`
  position: relative;
  flex: 0 0 ${LARGURA_EIXO_Y}px;
  height: ${MARGEM_TOPO + ALTURA_PLOT + ALTURA_ROTULO_X}px;

  span {
    position: absolute;
    right: 8px;
    font-size: 11px;
    line-height: 16px;
    color: ${({ theme }) => theme.cores.inkMuted};
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
`;

export const Rolagem = styled.div`
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;

  svg {
    display: block;
  }
`;

// Valores do ponto sob o mouse (ou tocado), acima do gráfico
export const Painel = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px ${({ theme }) => theme.espaco.lg};
  min-height: 20px;
  font-size: ${({ theme }) => theme.fonte.caption};
  color: ${({ theme }) => theme.cores.inkSecondary};

  strong {
    color: ${({ theme }) => theme.cores.ink};
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  .dica {
    color: ${({ theme }) => theme.cores.inkMuted};
  }
`;

export const ItemPainel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

export const Marcador = styled.span<{ $cor: string }>`
  display: inline-block;
  flex: 0 0 auto;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background-color: ${({ $cor }) => $cor};
`;

export const LegendaContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px ${({ theme }) => theme.espaco.lg};
  font-size: ${({ theme }) => theme.fonte.caption};
  color: ${({ theme }) => theme.cores.inkSecondary};
`;

// Rosca: gráfico e legenda lado a lado no desktop, empilhados no mobile
export const RoscaContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.espaco.xl};

  ${media.mobile} {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const RoscaGrafico = styled.div`
  position: relative;
  flex: 0 0 auto;
  align-self: center;

  path {
    cursor: pointer;
    transition: opacity 0.15s ease, stroke-width 0.15s ease;
  }
`;

export const RoscaCentro = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 48px;
  text-align: center;
  pointer-events: none;

  span {
    font-size: ${({ theme }) => theme.fonte.caption};
    color: ${({ theme }) => theme.cores.inkSecondary};
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  strong {
    font-size: ${({ theme }) => theme.fonte.h3};
    font-weight: 600;
    color: ${({ theme }) => theme.cores.ink};
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
`;

export const RoscaLegenda = styled.ul`
  flex: 1;
  min-width: 0;
  max-width: 480px;
  list-style: none;
`;

export const LinhaLegenda = styled.li<{ $ativo: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.espaco.sm};
  padding: 6px ${({ theme }) => theme.espaco.sm};
  border-radius: ${({ theme }) => theme.raio.md};
  background-color: ${({ $ativo, theme }) => ($ativo ? theme.cores.selecionado : 'transparent')};
  cursor: pointer;

  .rotulo {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${({ theme }) => theme.cores.ink};
  }

  .valor {
    font-weight: 600;
    color: ${({ theme }) => theme.cores.ink};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  .percentual {
    width: 44px;
    text-align: right;
    font-size: ${({ theme }) => theme.fonte.caption};
    color: ${({ theme }) => theme.cores.inkMuted};
    font-variant-numeric: tabular-nums;
  }

  ${media.mobile} {
    min-height: ${({ theme }) => theme.alturaControle.mobile};
  }
`;

// Ranking: rótulo e valor em texto, barra fina abaixo
export const Ranking = styled.ul`
  max-width: 760px;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.espaco.md};
  list-style: none;
`;

export const ItemRanking = styled.li`
  display: flex;
  flex-direction: column;
  gap: 4px;

  .linha {
    display: flex;
    align-items: baseline;
    gap: ${({ theme }) => theme.espaco.sm};
  }

  .rotulo {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${({ theme }) => theme.cores.ink};
  }

  .valor {
    font-weight: 600;
    color: ${({ theme }) => theme.cores.ink};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;

    &.negativo {
      color: ${({ theme }) => theme.cores.critical};
    }
  }
`;

export const Trilho = styled.div<{ $comNegativo: boolean }>`
  display: flex;
  height: 8px;
  border-radius: 4px;
  background-color: ${({ $comNegativo, theme }) => ($comNegativo ? 'transparent' : theme.cores.surface)};

  .metade {
    flex: 1;
    display: flex;
  }

  .metade.esquerda {
    justify-content: flex-end;
  }

  .zero {
    width: 1px;
    background-color: ${({ theme }) => theme.cores.inkMuted};
  }
`;

export const Barra = styled.div<{ $largura: number; $cor: string }>`
  width: ${({ $largura }) => Math.max($largura * 100, 1)}%;
  height: 8px;
  border-radius: 4px;
  background-color: ${({ $cor }) => $cor};
`;
